import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { UserCircle, Building2, TrendingUp, Users } from 'lucide-react';

import GeneralDataTab from './customer-form/GeneralDataTab';
import CompanyCodeTab from './customer-form/CompanyCodeTab';
import SalesAreaTab from './customer-form/SalesAreaTab';
import PartnerFunctionTab from './customer-form/PartnerFunctionTab';
import { useCustomerForm } from './customer-form/useCustomerForm';

/**
 * Reusable Customer Form. All state/loading/handlers live in `useCustomerForm`;
 * this component is the presentational shell (header + tabbed views).
 * @param {'create' | 'view' | 'edit'} mode - Form mode
 * @param {string} customerId - Customer ID for view/edit modes
 */
const CustomerForm = ({ mode = 'create', customerId = null }) => {
  const { ctx, handleSubmit, pageHeader, activeTab, setActiveTab } = useCustomerForm(mode, customerId);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="customer-form">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pageHeader.title}</h1>
            <p className="text-sm text-gray-600">{pageHeader.subtitle}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="general" className="flex items-center gap-2">
                  <UserCircle className="w-4 h-4" />
                  General Data
                </TabsTrigger>
                <TabsTrigger value="company" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Code
                </TabsTrigger>
                <TabsTrigger value="sales" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Sales & Distribution
                </TabsTrigger>
                <TabsTrigger value="partner" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Partner Functions
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general">
                <GeneralDataTab ctx={ctx} />
              </TabsContent>
              <TabsContent value="company">
                <CompanyCodeTab ctx={ctx} />
              </TabsContent>
              <TabsContent value="sales">
                <SalesAreaTab ctx={ctx} />
              </TabsContent>
              <TabsContent value="partner">
                <PartnerFunctionTab ctx={ctx} />
              </TabsContent>
            </Tabs>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerForm;
